/** Game constants: physics, movement, knockback and falling, fog. Part of src/constants.ts (re-exported there; import from '../constants'). */

// =============================================================================
// PHYSICS
// =============================================================================

export const G = 12;                    // gravitational constant
export const PHYS_DT = 1 / 120;        // fixed physics timestep (seconds)
export const MAX_PROJECTILE_TIME = 60;  // seconds before projectile times out
/**
 * 64.3: the continuous ruleset ends a lost shot well before classic's 60 s (MAX_PROJECTILE_TIME), so a miss doesn't
 * hold a round, or a live battle's airspace, for a minute (round simulator `lostAfterSteps`). A starting value, not a
 * ruling: tune in play.
 */
export const CONTINUOUS_LOST_SHOT_SECONDS = 20;
export const CLUSTER_LAUNCH_DELAY_STEPS = 18; // physics steps between cluster bomblets (~150 ms at 120 Hz) — step-based so P2P peers agree
export const OUT_OF_BOUNDS = 500;       // distance from origin to count as lost
export const PLANET_DESTROY_THRESHOLD = 0.2; // fraction of initial mass

// Terrain height envelope (fractions of planet radius). Collision uses
// radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION) as its bounding shell, so any
// terrain change must keep the heightmap inside this envelope.
// Mirrored in wasm-physics/src/lib.rs — keep in sync.
export const MAX_TERRAIN_HEIGHT_FRACTION = 0.4;  // mounds/peaks clamp here
export const MAX_CRATER_DEPTH_FRACTION = 0.5;    // craters never dig below -0.5R

// Megaplanet power scaling
export const MEGAPLANET_THRUST_RADIUS = 30;  // planets >= this radius get boosted launch velocity
export const MEGAPLANET_POWER_DIVISOR = 10;  // powerMult = radius / divisor (tuning knob)

// =============================================================================
// MOVEMENT
// =============================================================================

export const BASE_MOVEMENT_RANGE = 4.0;  // units per turn (roughly 1/8 of medium planet circumference)
export const JUMP_BASE_COST = 3.0;       // minimum movement points for any jump
export const JUMP_DISTANCE_FACTOR = 0.08; // additional cost per unit of distance
export const MELEE_RANGE = 1.2;          // max distance for melee attack
export const MELEE_DAMAGE = 25;          // base melee damage
export const MELEE_MOVEMENT_COST = 1.0;  // movement points consumed by melee attack

// Ballistic jump parameters
export const JUMP_VELOCITY_PER_MOVEMENT = 10.0;  // velocity units per movement point spent
export const JUMP_MIN_COST = 0.5;               // minimum movement points to jump at all
export const WALK_SPEED = 3.0;                   // arc-distance units per second for walk animation
export const FIRE_MOVEMENT_COST = 0.5;           // movement points consumed when firing
export const PLANT_FLAG_MOVEMENT_COST = 1.0;     // movement points to plant a flag
export const STARTING_FLAGS = 1;                  // flags each player starts with
export const JUMPPACK_FUEL_COST = 5;              // CBP per fuel unit (pre-game)
export const JUMPPACK_VELOCITY_BONUS = 30.0;      // flat velocity bonus when fuel burned

// =============================================================================
// KNOCKBACK & FALLING
// =============================================================================

export const KNOCKBACK_FORCE = 12.0;     // base knockback velocity multiplier
export const KNOCKBACK_FALLOFF = 0.5;    // how quickly knockback decreases with distance (power)
/** The knockback circle: a blast pushes people out to its radius × this (the damage circle is × 2.2). */
export const KNOCKBACK_RADIUS_FACTOR = 3.0;
export const KNOCKBACK_UPWARD_BIAS = 0.6; // how much to bias knockback toward "up" (0 = none, 1 = all vertical)
export const KNOCKBACK_MIN_LAUNCH_STRENGTH = 3.0; // minimum strength to launch player airborne (even short arc)
// Cover (37.18): a unit below a crater rim is sheltered from shots landing outside it. The rim is the highest
// terrain between the unit and the impact, above the unit's own footing; at COVER_FULL_RIM (units) and above the
// knockback is cut by COVER_KNOCKBACK_REDUCTION, blast damage by COVER_DAMAGE_REDUCTION (0 = knockback only).
/** K3 (37.20 ledger #4): bots know an objective object's spot only where their team has revealed the ground (false = the old omniscient objectives, for A/B). */
export const BOT_HONEST_OBJECTIVES = true;
export const COVER_FULL_RIM = 1.0;
export const COVER_KNOCKBACK_REDUCTION = 0.7;
export const COVER_DAMAGE_REDUCTION = 0;
export const COVER_SAMPLES = 4;                  // terrain samples along the arc between unit and impact
export const FALL_DAMAGE_FACTOR = 0.08;  // damage per unit of impact velocity (gentle landings)
export const SPACE_DEATH_DISTANCE = 80;  // distance from all planets before player dies in space
/**
 * A body is lost in space past SPACE_DEATH_DISTANCE from every planet's centre — or past this many of a planet's radii,
 * when that is further: a megaplanet's ground (radius 78.5, its relief to 1.4 radii) stood past 80, so every person who
 * left it (a jump, a blast's push) was lost on the first airborne tick. Every planet under 40 keeps 80 exactly.
 */
export const SPACE_DEATH_RADII = 2;
// Phase 57 — perception and alert (S0 stealth); only read when a match has an alert layer
export const PERCEPTION_CONE_HALF_ANGLE = Math.PI / 3;   // 120° cone for a guard with a facing
export const PERCEPTION_RANGE_UNAWARE = 10;               // world units, eye to eye
export const PERCEPTION_RANGE_SUSPICIOUS = 18;
export const PERCEPTION_CLOSE_RANGE = 5;                  // a sighting this close goes straight to alert
export const ALERT_DECAY_TURNS_SUSPICIOUS = 3;            // suspicious → unaware after this many quiet turns
export const ALERT_DECAY_TURNS_ALERT = 6;                 // alert → suspicious
export const NOISE_RADIUS_SHOT = 45;                      // planets within this of an impact grow suspicious
/** Seconds a unit may stay airborne before it counts as lost in space (a stable orbit inside SPACE_DEATH_DISTANCE would otherwise hang the match). */
export const MAX_AIRBORNE_TIME = 60;
export const LANDING_VELOCITY_THRESHOLD = 2.0; // velocity below which landing is "soft"

// =============================================================================
// FOG OF WAR
// =============================================================================

export const PROBE_REVEAL_RADIUS = 12.0;
export const FOG_DARK_COLOR = { r: 0.06, g: 0.06, b: 0.08 };
export const FOG_STALE_TURNS = 5;       // revealed terrain starts to look old after this many turns
export const FOG_STALE_FADE_TURNS = 10; // ... and reaches full dimness this many turns later
export const FOG_STALE_MIN_BRIGHTNESS = 0.6;
export const GHOST_MARKER_MAX_AGE = 20;
export const EXPOSURE_MARKER_MAX_AGE = 3;
export const SPOTTED_NOTIFICATION_COOLDOWN = 2;
/**
 * Line of sight (core/los.ts). A player's eye — and the point other players
 * see — sits LOS_EYE_HEIGHT above the exact terrain surface. Mirrored in
 * wasm-physics/src/lib.rs (`los_eye_height()` is asserted equal in tests).
 */
export const LOS_EYE_HEIGHT = 0.9;
/** A person's body height in world units at classic scale (core/scaleProfile.ts scales it by k_human). */
export const PERSON_BODY_HEIGHT = 1.1;
/** A tower's eye, in person heights above the ground it stands on (WORKBOARD I1; the owner: two people, humble). */
export const TOWER_PERSON_HEIGHTS = 2;
/** Azimuth bins of the own-planet skyline used to occlude remote planets. */
export const LOS_SKYLINE_BINS = 180;
